import { useAdminer } from './chunk-MB2JPXXN.js';
export { useAdminer, useHasPermission, useSlot } from './chunk-MB2JPXXN.js';
import { AuthContext } from './chunk-LUC3HWI7.js';
export { AuthContext, getSafeReturnTo, useAuth } from './chunk-LUC3HWI7.js';
import { defaultLinkComponent } from './chunk-46OQMWYR.js';
export { defaultLinkComponent } from './chunk-46OQMWYR.js';
import { AdminerContext } from './chunk-3G2YSMCN.js';
export { useT } from './chunk-3G2YSMCN.js';
import { ApiError } from './chunk-LEGW4QDU.js';
import { resolveTranslator } from './chunk-RRFUPILC.js';
export { resolveTranslator } from './chunk-RRFUPILC.js';
export { defaultDictionary } from './chunk-4DPTK2CR.js';
import { useMemo } from 'react';
import { jsx } from 'react/jsx-runtime';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';

// src/internal/permissions/defaultPermissions.ts
var defaultPermissions = {
  has: () => true
};
function AdminerProvider({
  children,
  api,
  auth,
  permissions,
  i18n,
  components,
  LinkComponent
}) {
  const value = useMemo(
    () => ({
      api,
      auth,
      permissions: permissions ?? defaultPermissions,
      t: resolveTranslator(i18n),
      components: components ?? {},
      LinkComponent: LinkComponent ?? defaultLinkComponent
    }),
    [api, auth, permissions, i18n, components, LinkComponent]
  );
  return /* @__PURE__ */ jsx(AdminerContext.Provider, { value, children });
}

// src/internal/api/createFetchClient.ts
function createFetchClient(opts) {
  const baseUrl = opts.baseUrl.replace(/\/+$/, "");
  const fetchImpl = opts.fetchImpl ?? fetch;
  async function request(input, init = {}) {
    const url = typeof input === "string" && input.startsWith("/") ? baseUrl + input : input;
    const headers = new Headers(opts.headers);
    new Headers(init.headers).forEach((v, k) => headers.set(k, v));
    const res = await fetchImpl(url, {
      credentials: "include",
      ...init,
      headers
    });
    if (res.status === 401) {
      opts.onUnauthorized?.();
      throw new ApiError(401, null);
    }
    if (!res.ok) {
      const ct2 = res.headers.get("content-type") ?? "";
      let body;
      if (ct2.includes("application/json")) {
        body = await res.json().catch(() => null);
      } else {
        body = await res.text().catch(() => null);
      }
      throw new ApiError(res.status, body);
    }
    const ct = res.headers.get("content-type") ?? "";
    if (ct.includes("application/json")) {
      return await res.json();
    }
    return await res.text();
  }
  return { baseUrl, request };
}
function useMe() {
  const { auth } = useAdminer();
  if (!auth) {
    throw new Error(
      "useMe(): no auth adapter configured on <AdminerProvider>"
    );
  }
  return useQuery({
    queryKey: ["adminer", "auth", "me"],
    queryFn: () => auth.me(),
    staleTime: Infinity,
    // Auth checks should not retry: a 401 is definitive (the adapter
    // maps it to null), and retrying non-auth errors would cause a
    // multi-second delay before the login page is shown.
    retry: false
  });
}
function AuthProvider({ children }) {
  const { data: user, isLoading } = useMe();
  return /* @__PURE__ */ jsx(AuthContext.Provider, { value: { user, isLoading }, children });
}
function useLogin() {
  const { auth } = useAdminer();
  const qc = useQueryClient();
  if (!auth) {
    throw new Error(
      "useLogin(): no auth adapter configured on <AdminerProvider>"
    );
  }
  return useMutation({
    mutationFn: (creds) => auth.login(creds),
    onSuccess: (user) => {
      qc.setQueryData(["adminer", "auth", "me"], user);
    }
  });
}
function useLogout() {
  const { auth } = useAdminer();
  const qc = useQueryClient();
  if (!auth) {
    throw new Error(
      "useLogout(): no auth adapter configured on <AdminerProvider>"
    );
  }
  return useMutation({
    mutationFn: () => auth.logout(),
    onSettled: () => {
      qc.setQueryData(["adminer", "auth", "me"], null);
      qc.clear();
    }
  });
}
function useChangePassword() {
  const { auth } = useAdminer();
  if (!auth) {
    throw new Error(
      "useChangePassword(): no auth adapter configured on <AdminerProvider>"
    );
  }
  if (!auth.changePassword) {
    throw new Error(
      "useChangePassword(): the configured auth adapter does not implement changePassword()"
    );
  }
  return useMutation({
    mutationFn: (input) => auth.changePassword(input)
  });
}

// src/index.ts
var ADMINER_VERSION = "0.0.0";

export { ADMINER_VERSION, AdminerProvider, AuthProvider, createFetchClient, defaultPermissions, useChangePassword, useLogin, useLogout, useMe };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map